import React, { useEffect } from 'react';
import { Modal, Form, Input, Button, message, DatePicker, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../../theme/hooks';
import { addCustomer, updateCustomer } from '../../services/customerService';
import locale from 'antd/es/date-picker/locale/de_DE';
import { LoadScript, Autocomplete } from '@react-google-maps/api';
import dayjs from 'dayjs';

const GOOGLE_MAPS_LIBRARIES = ['places'];

const useCustomerFormStyles = () => useStyles((theme) => ({
  form: {
    maxWidth: '100%'
  },
  section: {
    marginBottom: theme.spacing.lg
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing.md
  },
  autocompleteInput: {
    width: '100%',
    height: '32px',
    padding: '4px 11px',
    borderRadius: '6px',
    border: '1px solid #d9d9d9',
    '&:hover': {
      borderColor: '#4096ff',
    },
    '&:focus': {
      borderColor: '#4096ff',
      boxShadow: '0 0 0 2px rgba(5, 145, 255, 0.1)',
      outline: 'none',
    }
  }
}));

const CustomerForm = ({ visible, onCancel, onSuccess, initialData = null }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const styles = useCustomerFormStyles();
  const [autocomplete, setAutocomplete] = React.useState(null);
  const isEditing = !!initialData;

  useEffect(() => {
    if (initialData && visible) {
      // Convert date string to dayjs object for DatePicker
      const formData = {
        ...initialData,
        date_of_birth: initialData.date_of_birth ? dayjs(initialData.date_of_birth) : null
      };
      form.setFieldsValue(formData);
    } else if (!initialData && visible) {
      form.resetFields();
    }
  }, [initialData, visible, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values.date_of_birth) {
        values.date_of_birth = values.date_of_birth.format('YYYY-MM-DD');
      }

      let response;
      if (isEditing) {
        response = await updateCustomer({ ...values, id: initialData.id });
      } else {
        response = await addCustomer(values);
      }
      
      message.success(isEditing ? t('customer.updateSuccess') : t('customer.createSuccess'));
      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      message.error(isEditing ? t('customer.updateError') : t('customer.createError'));
    }
  };

  const onPlaceSelected = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      
      let streetName = '';
      let streetNumber = '';
      let postalCode = '';
      let city = '';

      // Extract address components
      place.address_components.forEach(component => {
        const types = component.types;
        
        if (types.includes('route')) {
          streetName = component.long_name;
        }
        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }
        if (types.includes('postal_code')) {
          postalCode = component.long_name;
        }
        if (types.includes('locality')) {
          city = component.long_name;
        }
      });

      // Update form fields
      form.setFieldsValue({
        street: streetName,
        house_number: streetNumber,
        zip_code: postalCode,
        city: city
      });
    }
  };

  return (
    <Modal
      title={isEditing ? t('customer.modal.titleEdit') : t('customer.modal.title')}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={GOOGLE_MAPS_LIBRARIES}
      >
        <Form
          form={form}
          layout="vertical"
          style={styles.form}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('customer.fields.name')}
                rules={[{ required: true, message: t('common.validation.required') }]}
              >
                <Input maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="first_name"
                label={t('customer.fields.firstName')}
              >
                <Input maxLength={255} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label={t('customer.fields.email')}
                rules={[
                  { type: 'email', message: t('common.validation.invalidEmail') }
                ]}
              >
                <Input maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="mobile"
                label={t('customer.fields.mobile')}
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label={t('customer.fields.phone')}
              >
                <Input maxLength={50} />
              </Form.Item>            
            </Col>
            <Col span={12}>
              <Form.Item
                name="date_of_birth"
                label={t('customer.fields.dateOfBirth')}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="DD.MM.YYYY"
                  locale={locale}
                />
              </Form.Item>
            </Col>          
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={t('customer.fields.addressSearch')}
              >
                <Autocomplete
                  onLoad={setAutocomplete}
                  onPlaceChanged={onPlaceSelected}
                  restrictions={{ country: ['de', 'at', 'ch', 'nl', 'be', 'lu', 'dk', 'pl', 'cz'] }}
                >
                  <input
                    type="text"
                    placeholder={t('customer.fields.searchPlaceholder')}
                    style={styles.autocompleteInput}
                  />
                </Autocomplete>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="street"
                label={t('customer.fields.street')}
              >
                <Input maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="house_number"
                label={t('customer.fields.houseNumber')}
              >
                <Input maxLength={20} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="zip_code"
                label={t('customer.fields.zipCode')}
              >
                <Input maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="city"
                label={t('customer.fields.city')}
              >
                <Input maxLength={255} />
              </Form.Item>
            </Col>
          </Row>

          <div style={styles.buttons}>
            <Button onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button type="primary" onClick={handleSubmit}>
              {isEditing ? t('common.save') : t('common.create')}
            </Button>
          </div>
        </Form>
      </LoadScript>
    </Modal>
  );
};

export default CustomerForm;
